// @file Vuex Store module for Snackbars displayed by app_global_snackbar.vue
import uniqueId from 'lodash.uniqueid'
import { Module } from 'vuex'
import window from 'bits/global'

const DEFAULT_TIMEOUT = 5000
const PAUSE_BETWEEN_SNACKBARS = 200

interface SnackbarNotification {
  uid: string
  message: string
  actionText?: string
  actionUrl?: string
  actionName?: string
  timeout?: number
  openTimerId?: number | null
  closeTimerId?: number | null
  persist?: boolean
  showCloseButton?: boolean
}

interface SnackbarsState {
  current: SnackbarNotification | null
  queue: SnackbarNotification[]
}

const SnackbarsModule: Module<SnackbarsState, object> = {
  namespaced: true,
  state: {
    current: null,
    queue: [],
  },
  getters: {
    hasPending(state): boolean {
      return state.queue.length > 0
    },
    nextSnackbar(state, getters): SnackbarNotification | null {
      if (!getters.hasPending) return null
      return state.queue[0]
    },
    currentSnackbar(state): SnackbarNotification | null {
      return state.current
    },
    isCoolingDown(state): boolean {
      return !!(state.current && state.current.openTimerId)
    },
    xSnackbar(state, getters): boolean {
      return getters.currentSnackbar && !getters.isCoolingDown
    },
  },
  mutations: {
    CLEAR_CURRENT_SNACKBAR(state): void {
      state.current = null
    },
    REVEAL_SNACKBAR(state): void {
      if (!state.current) return
      state.current.openTimerId = null
    },
    NEXT_SNACKBAR(state, payload): void {
      if (state.queue.length <= 0) return
      const nextSnackbar = state.queue.shift()
      state.current = { ...nextSnackbar, ...payload }
    },
    ENQUEUE_SNACKBAR(state, notification): void {
      state.queue = [...state.queue, notification]
    },
    REMOVE_FROM_QUEUE(state, uid): void {
      state.queue = state.queue.filter((notification): boolean => notification.uid !== uid)
    },
  },
  actions: {
    clearSnackbar({ commit, getters, dispatch }, uid): void {
      const currentSnackbar = getters.currentSnackbar
      if (currentSnackbar && currentSnackbar.uid === uid) {
        dispatch('nextSnackbar')
      } else {
        commit('REMOVE_FROM_QUEUE', uid)
      }
    },
    clearCurrentSnackbar({ state, commit, getters }): void {
      if (!getters.currentSnackbar) return
      const current = state.current as SnackbarNotification
      if (current.openTimerId) clearTimeout(current.openTimerId)
      if (current.closeTimerId) clearTimeout(current.closeTimerId)
      commit('CLEAR_CURRENT_SNACKBAR')
    },
    nextSnackbar({ dispatch, commit, getters }): void {
      const hasPreviousSnackbar = !!getters.currentSnackbar
      if (hasPreviousSnackbar) dispatch('clearCurrentSnackbar')
      if (!getters.hasPending) return
      const nextSnackbar = getters.nextSnackbar

      let closeTimerId: number | null = null
      if (!nextSnackbar.persist) {
        const closeTimeout =
          (nextSnackbar.timeout || DEFAULT_TIMEOUT) + (hasPreviousSnackbar ? PAUSE_BETWEEN_SNACKBARS : 0)
        closeTimerId = window.setTimeout((): Promise<void> => dispatch('nextSnackbar'), closeTimeout)
      }

      let openTimerId: number | null = null
      if (hasPreviousSnackbar) {
        openTimerId = window.setTimeout((): void => commit('REVEAL_SNACKBAR'), PAUSE_BETWEEN_SNACKBARS)
      }

      commit('NEXT_SNACKBAR', { closeTimerId, openTimerId })
    },
    setSnackbar({ commit, getters, dispatch }, payload: SnackbarNotification): string {
      const uid = uniqueId('snack_')
      commit('ENQUEUE_SNACKBAR', { showCloseButton: false, ...payload, uid })
      if (!getters.currentSnackbar || (getters.currentSnackbar && getters.currentSnackbar.persist)) {
        dispatch('nextSnackbar')
      }
      return uid
    },
    executeCurrentSnackbarAction({ dispatch, getters }): void {
      const currentSnackbar = getters.currentSnackbar
      if (!currentSnackbar) return
      if (currentSnackbar.actionName) {
        dispatch(currentSnackbar.actionName, null, { root: true })
      }
      dispatch('nextSnackbar')
    },
  },
}

export default SnackbarsModule
export { SnackbarNotification, SnackbarsState }
